import { useEffect, useState } from "react";
import { motion } from "motion/react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import { adminStyles } from "./adminStyles";

export default function AdminNewsPage({
                                          primaryColor = "#8D6E63",
                                          secondaryColor = "#A1887F",
                                      }) {
    const navigate = useNavigate();

    const [news, setNews] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        loadNews();
    }, []);

    async function loadNews() {
        setIsLoading(true);
        setErrorMessage("");

        const { data, error } = await supabase
            .from("news")
            .select("*")
            .order("created_at", { ascending: false });

        if (error) {
            console.error("Fehler beim Laden der Beiträge:", error);
            setErrorMessage("Die Beiträge konnten nicht geladen werden.");
            setIsLoading(false);
            return;
        }

        setNews(data || []);
        setIsLoading(false);
    }

    async function handleDelete(id) {
        const confirmed = window.confirm(
            "Möchtest du diesen Beitrag wirklich löschen?"
        );

        if (!confirmed) return;

        const { error } = await supabase
            .from("news")
            .delete()
            .eq("id", id);

        if (error) {
            console.error("Fehler beim Löschen:", error);
            alert("Der Beitrag konnte nicht gelöscht werden.");
            return;
        }

        setNews((currentNews) => currentNews.filter((item) => item.id !== id));
    }

    async function handleTogglePublished(item) {
        const nextPublishedState = !item.published;

        const { error } = await supabase
            .from("news")
            .update({
                published: nextPublishedState,
                published_at: nextPublishedState
                    ? item.published_at || new Date().toISOString()
                    : null,
                updated_at: new Date().toISOString(),
            })
            .eq("id", item.id);

        if (error) {
            console.error("Fehler beim Aktualisieren:", error);
            alert("Der Veröffentlichungsstatus konnte nicht geändert werden.");
            return;
        }

        setNews((currentNews) =>
            currentNews.map((newsItem) =>
                newsItem.id === item.id
                    ? {
                        ...newsItem,
                        published: nextPublishedState,
                        published_at: nextPublishedState
                            ? item.published_at || new Date().toISOString()
                            : null,
                        updated_at: new Date().toISOString(),
                    }
                    : newsItem
            )
        );
    }

    const formatDate = (dateValue) => {
        if (!dateValue) return "Nicht veröffentlicht";

        return new Date(dateValue).toLocaleDateString("de-DE", {
            day: "2-digit",
            month: "long",
            year: "numeric",
        });
    };

    return (
        <main style={adminStyles.dashboardPage}>
            <div style={adminStyles.dashboardContainer}>
                <header style={adminStyles.dashboardHeader}>
                    <div>
                        <h1 style={{ ...adminStyles.title, color: primaryColor }}>
                            Neuigkeiten verwalten
                        </h1>
                        <p style={adminStyles.subtitle}>
                            Erstelle, bearbeite und veröffentliche Beiträge für die Webseite.
                        </p>
                    </div>

                    <div
                        style={{
                            display: "flex",
                            gap: "0.75rem",
                            flexWrap: "wrap",
                        }}
                    >
                        <button
                            type="button"
                            onClick={() => navigate("/admin")}
                            style={{
                                ...adminStyles.secondaryButton,
                                backgroundColor: secondaryColor,
                            }}
                        >
                            Dashboard
                        </button>

                        <button
                            type="button"
                            onClick={() => navigate("/admin/news/new")}
                            style={{
                                ...adminStyles.secondaryButton,
                                backgroundColor: primaryColor,
                            }}
                        >
                            Neuer Beitrag
                        </button>
                    </div>
                </header>

                {errorMessage && (
                    <div style={adminStyles.error}>{errorMessage}</div>
                )}

                {isLoading ? (
                    <p style={{ color: "#666" }}>Beiträge werden geladen...</p>
                ) : news.length === 0 ? (
                    <motion.div
                        style={adminStyles.dashboardCard}
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4 }}
                    >
                        <h2 style={{ marginTop: 0, color: primaryColor }}>
                            Noch keine Beiträge vorhanden
                        </h2>
                        <p style={{ color: "#666", lineHeight: 1.7 }}>
                            Lege den ersten Beitrag an, um ihn später auf der Webseite
                            anzeigen zu lassen.
                        </p>

                        <button
                            type="button"
                            onClick={() => navigate("/admin/news/new")}
                            style={{
                                ...adminStyles.secondaryButton,
                                backgroundColor: primaryColor,
                                marginTop: "1rem",
                            }}
                        >
                            Beitrag erstellen
                        </button>
                    </motion.div>
                ) : (
                    <div
                        style={{
                            display: "grid",
                            gap: "1rem",
                        }}
                    >
                        {news.map((item, index) => (
                            <motion.article
                                key={item.id}
                                style={{
                                    ...adminStyles.dashboardCard,
                                    display: "grid",
                                    gridTemplateColumns:
                                        "minmax(0, 1fr) auto",
                                    gap: "1rem",
                                    alignItems: "center",
                                }}
                                initial={{ opacity: 0, y: 18 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{
                                    duration: 0.35,
                                    delay: index * 0.04,
                                }}
                            >
                                <div style={{ minWidth: 0 }}>
                                    <span
                                        style={{
                                            display: "inline-block",
                                            marginBottom: "0.65rem",
                                            padding: "0.3rem 0.75rem",
                                            borderRadius: "999px",
                                            backgroundColor: item.published
                                                ? `${primaryColor}18`
                                                : "#f1f1f1",
                                            color: item.published
                                                ? primaryColor
                                                : "#777",
                                            fontSize: "0.85rem",
                                            fontWeight: 700,
                                        }}
                                    >
                                        {item.published
                                            ? "Veröffentlicht"
                                            : "Entwurf"}
                                    </span>

                                    <h2
                                        style={{
                                            margin: "0 0 0.5rem",
                                            color: primaryColor,
                                            fontSize: "1.35rem",
                                            lineHeight: 1.35,
                                            wordBreak: "break-word",
                                        }}
                                    >
                                        {item.title}
                                    </h2>

                                    <p
                                        style={{
                                            margin: "0 0 0.75rem",
                                            color: "#666",
                                            lineHeight: 1.6,
                                        }}
                                    >
                                        {item.excerpt || "Keine Kurzbeschreibung vorhanden."}
                                    </p>

                                    <p
                                        style={{
                                            margin: 0,
                                            color: "#888",
                                            fontSize: "0.9rem",
                                        }}
                                    >
                                        {item.published
                                            ? `Veröffentlicht am ${formatDate(item.published_at)}`
                                            : "Noch nicht veröffentlicht"}
                                    </p>
                                </div>

                                <div
                                    style={{
                                        display: "flex",
                                        gap: "0.5rem",
                                        flexWrap: "wrap",
                                        justifyContent: "flex-end",
                                    }}
                                >
                                    <button
                                        type="button"
                                        onClick={() =>
                                            handleTogglePublished(item)
                                        }
                                        style={{
                                            ...adminStyles.secondaryButton,
                                            backgroundColor: item.published
                                                ? "#777"
                                                : primaryColor,
                                        }}
                                    >
                                        {item.published
                                            ? "Offline nehmen"
                                            : "Veröffentlichen"}
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() =>
                                            navigate(`/admin/news/${item.id}/edit`)
                                        }
                                        style={{
                                            ...adminStyles.secondaryButton,
                                            backgroundColor: secondaryColor,
                                        }}
                                    >
                                        Bearbeiten
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => handleDelete(item.id)}
                                        style={{
                                            ...adminStyles.secondaryButton,
                                            backgroundColor: "#b94a48",
                                        }}
                                    >
                                        Löschen
                                    </button>
                                </div>
                            </motion.article>
                        ))}
                    </div>
                )}
            </div>
        </main>
    );
}